import { store } from '../store';
import { setLoadingMyClubs } from '../store/slices/generalSlice';
import { setUpdatingUpcomingEvents, setUpdatingUpcomingEventsToday } from '../store/slices/upcomingEventSlice';
import { firebaseAuth } from '../firebase/firebaseconfig';
import { deleteToken, getToken } from '../firebaseMessagingUtils';
import { Dialog } from '@capacitor/dialog';
import { Capacitor } from '@capacitor/core';
import config from '../../config.json';

export const getUser = async () => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/getUser`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
    return response.json();
}

export const checkForNewerVersion = async (version: string) => {

    const platform = Capacitor.getPlatform();

    const url = `${process.env.API_URL}/api/app/checkVersion/${version}/${platform}`;
    const response = await fetch(url, {
        method: 'GET'
    });
    return response.json();
}

export const updateUserInOrOutEvent = async (eventId: string, inOut: boolean | null, userId: string, clubId: string, includeEventQueueUpdate: boolean = false) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/signInOrOutForEvent`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(
            {
                eventId,
                inOut,
                userId,
                clubId,
                includeEventQueueUpdate: includeEventQueueUpdate
            }
        )
    });
    return response.json();
}

export const joinEventQueue = async (eventId: string, clubId: string, leave: boolean = false) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/joinEventQueue`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(
            {
                eventId,
                clubId,
                leave
            }
        )
    });
    return response.json();
}

export const createEvent = async (event: any) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/createEvent`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(
            {
                clubId: event?.clubId || "",
                date: event?.date || "",
                description: event?.description || "",
                eventLength: event?.eventLength || null,
                location: event?.location || null,
                isWeekly: event?.isWeekly || false,
                repeatCount: event?.repeatCount || null,
                name: event?.name || "",
                showWeather: event?.showWeather || false,
                maxJoinCount: event?.maxJoinCount || null,
                joinDeadline: event?.joinDeadline || { hours: 0, minutes: 0 }
            }
        )
    });
    return response.json();
}

export const getEventWeatherData = async (eventId: string, clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/getWeatherData/${clubId}/${eventId}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
    return response.json();
}

export const storeFCMToken = async () => {
    try {
        const auth = await firebaseAuth();
        const currentUser = auth.currentUser;

        if (!currentUser) {
            throw new Error('No authenticated user found');
        }

        let fcm = ""

        if (Capacitor.isNativePlatform()) {
            fcm = await getToken();
        }

        if (!fcm) {
            return;
        }

        const token = await currentUser.getIdToken();

        const url = `${process.env.API_URL}/api/user/storeFCMToken`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                fcmToken: fcm
            })
        });
        return response.json();
    } catch (error) {
        console.error('Error storing FCM token:', error);
        return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
}

export const storeQrData = async (qrPlace: string) => {
    try {
        if (localStorage.getItem('ilmo_storedQrData') === 'true') {
            return { success: true };
        }

        const url = `${process.env.API_URL}/api/qr/${qrPlace}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        const res = await response.json();
        if (res && res.success) {
            localStorage.setItem('ilmo_storedQrData', 'true');
        }
        return res;
    } catch (error) {
        console.error('Error storing QR data:', error);
        return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
}

export const storeAppVersionInDB = async () => {
    try {
        const auth = await firebaseAuth();
        const currentUser = auth.currentUser;

        if (!currentUser) {
            throw new Error('No authenticated user found');
        }

        const token = await currentUser.getIdToken();

        const url = `${process.env.API_URL}/api/app/storeAppVersion`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                version: process.env.NATIVE_APP_VERSION,
                platform: Capacitor.getPlatform(),
            })
        });
        return response.json();
    } catch (error) {
        console.error('Error storing app version:', error);
        // Don't throw the error to prevent app crashes
        return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
}

export const removeFCMToken = async () => {
    try {
        const auth = await firebaseAuth();
        const currentUser = auth.currentUser;

        if (!currentUser) {
            throw new Error('No authenticated user found');
        }

        let fcmToken = "";
        if (Capacitor.isNativePlatform()) {
            fcmToken = await getToken();
        }

        const token = await currentUser.getIdToken();

        const url = `${process.env.API_URL}/api/user/removeFCMToken`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                fcmToken
            })
        });
        await deleteToken();
        return response.json();
    } catch (error) {
        console.error('Error storing FCM token:', error);
        // Don't throw the error to prevent app crashes
        return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
}

export const removeEvent = async (eventId: string, removeAllRepeatingEvents: boolean, clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/removeEvent`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(
            {
                eventId,
                removeAllRepeatingEvents,
                clubId
            }
        )
    });
    return response.json();
}

export const editEvent = async (eventId: string, changes: any, changeAllRepeatingEvents: boolean, dayDifference: number, clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/editEvent`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(
            {
                eventId,
                name: changes?.name,
                location: changes?.location,
                description: changes?.description,
                date: changes?.date,
                showWeather: changes?.showWeather,
                maxJoinCount: changes?.maxJoinCount,
                eventLength: changes?.eventLength,
                changeAllRepeatingEvents,
                dayDifference,
                clubId,
                includeEventQueueUpdate: true,
                joinDeadline: changes?.joinDeadline
            }
        )
    });
    return response.json();
}

export const updateUserEmail = async (email: string): Promise<any> => {
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    // Make the request
    const response = await fetch(`${process.env.API_URL}/api/user/updateEmail`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            email
        })
    });

    return await response.json();
}

export const updateUserDarkMode = async (darkMode: boolean): Promise<any> => {
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    // Make the request
    const response = await fetch(`${process.env.API_URL}/api/user/updateDarkMode`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            darkMode
        })
    });

    if (!response.ok) {
        throw new Error('Dark mode päivitys epäonnistui');
    }

    return await response.json();
}


export const updateUserDisplayName = async (displayName: string): Promise<any> => {
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    // Make the request
    const response = await fetch(`${process.env.API_URL}/api/user/updateDisplayName`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            displayName
        })
    });

    if (!response.ok) {
        throw new Error('Nimesi päivitys epäonnistui');
    }

    return await response.json();
}

export const updateUserBio = async (bio: string): Promise<any> => {
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    // Make the request
    const response = await fetch(`${process.env.API_URL}/api/user/updateBio`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            bio
        })
    });

    if (!response.ok) {
        throw new Error('Kuvauksen päivitys epäonnistui');
    }

    return await response.json();
}

export const changeClubImage = async (file: File, clubId: string): Promise<any> => {
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    // Create form data
    const formData = new FormData();
    formData.append('photo', file);

    // Make the request
    const response = await fetch(`${process.env.API_URL}/api/clubs/changeClubPicture/${clubId}`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData,
    });

    if (!response.ok) {
        throw new Error('Upload failed');
    }

    return await response.json();
}

export const changeProfilePicture = async (file: File): Promise<any> => {
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    // Create form data
    const formData = new FormData();
    formData.append('photo', file);

    // Make the request
    const response = await fetch(`${process.env.API_URL}/api/user/changeProfilePicture`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData,
    });

    return await response.json();
}

export const getMyClubs = async () => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const dispatch = store.dispatch;
    dispatch(setLoadingMyClubs(true));
    const url = `${process.env.API_URL}/api/clubs/getMyClubs`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
    dispatch(setLoadingMyClubs(false));
    return response.json();
}

export const getCorrelatingRepeatEvents = async (repeatCorrelationId: string, clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();
    const url = `${process.env.API_URL}/api/events/getCorrelatingRepeatEvents/${repeatCorrelationId}/${clubId}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
    return response.json();
}

export const getClubUsers = async (clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/users/getClubUsers/${clubId}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
    return response.json();
}

export const createClubInviteLink = async (clubId: string, expirationDate: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/invites/createClubInvitationLink`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            expirationDate,
            clubId
        })
    });
    return response.json();
}

export const sendFeedback = async (name: string, email: string, message: string) => {
    try {
        // Get the current Firebase user's ID token
        const auth = await firebaseAuth();
        const currentUser = auth.currentUser;

        let token = "";
        if (currentUser) {
            token = await currentUser.getIdToken();
        }

        const url = `${process.env.API_URL}/api/sendFeedback`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                name,
                email,
                message
            })
        });
        return await response.json();
    } catch (error) {
        console.error('Error sending feedback:', error);
        throw error;
    }
}

export const getShowFeedbackWidget = async () => {
    try {
        // Get the current Firebase user's ID token
        const auth = await firebaseAuth();
        const currentUser = auth.currentUser;

        let token = "";
        if (currentUser) {
            token = await currentUser.getIdToken();
        }

        const url = `${process.env.API_URL}/api/showFeedbackWidget`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json'
            }
        });
        return await response.json();
    } catch (error) {
        console.error('Error get showFeedbackWidget:', error);
        throw error;
    }
}

export const closeFeedbackWidget = async () => {
    try {
        // Get the current Firebase user's ID token
        const auth = await firebaseAuth();
        const currentUser = auth.currentUser;

        let token = "";
        if (currentUser) {
            token = await currentUser.getIdToken();
        }

        const url = `${process.env.API_URL}/api/closeFeedbackWidget`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json'
            }
        });
        return await response.json();
    } catch (error) {
        console.error('Error get showFeedbackWidget:', error);
        throw error;
    }
}

export const sendClubInvite = async (clubId: string, destination: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/invites/createClubInviteByMail`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            destination,
            clubId
        })
    });
    return response.json();
}

export const deleteClubInvite = async (clubId: string, inviteId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/invites/removeInvite`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            inviteId,
            clubId
        })
    });
    return response.json();
}

export const declineOrAcceptClubInvite = async (inviteId: string, accept: boolean, clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/invites/declineOrAcceptClubInvite`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            inviteId,
            accept,
            clubId
        })
    });
    return response.json();
}

export const getClubInvitesById = async (clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/invites/getInvitesByClubId`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            clubId
        })
    });
    return response.json();
}

export const getInviteById = async (inviteId: string, clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/invites/getInviteById`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            inviteId,
            clubId
        })
    });
    return response.json();
}

export const getEventGuests = async (eventId: string, clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/getEventGuests/${eventId}/${clubId}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });
    return response.json();
}

export const getUsersByIds = async (userIds: string[]) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/users/getUsersByIds`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(
            {
                userIds
            }
        )
    });
    return response.json();
}

export const getClubEvents = async (clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/getClubEvents/${clubId}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
    return response.json();
}

export const getSports = async () => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/sports/getSports`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
    return response.json();
}

export const setNotificationsAsHandled = async (notificationIds: string[]) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/user/setNotificationsHandled`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            notificationIds
        })
    });
    return response.json();
}


export const createClub = async (club: any) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/clubs/createClub`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(club)
    });
    return response.json();
}

export const createClubRole = async (clubId: string, roleName: string, userIds: string[]) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/clubs/addRole`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            clubId,
            roleName,
            assignedTo: userIds || []
        })
    });
    return response.json();
}

export const updateClubRole = async (clubId: string, roleId: string, roleName: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/clubs/updateRole`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            clubId,
            roleId,
            roleName
        })
    });
    return response.json();
}

export const assignRole = async (clubId: string, roleId: string, userIds: string[]) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/clubs/assignRole`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            clubId,
            roleId,
            userIds
        })
    });
    return response.json();
}

export const removeClubRole = async (clubId: string, roleId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/clubs/removeRole`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            clubId,
            roleId
        })
    });
    return response.json();
}

export const removeClubUser = async (removeUid: string, clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/clubs/removeClubUser`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            removeUid,
            clubId
        })
    });
    return response.json();
}

export const leaveClub = async (clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/clubs/leaveClub/${clubId}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
    return response.json();
}

export const updateClubUserAdminRights = async (updateUid: string, clubId: string, adminRights: boolean) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/clubs/updateClubUserAdminRights`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            updateUid,
            clubId,
            adminRights
        })
    });
    return response.json();
}

export const getMyEventsToday = async () => {
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }


    store.dispatch(setUpdatingUpcomingEventsToday(true));


    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/getMyEventsToday`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    store.dispatch(setUpdatingUpcomingEventsToday(false));

    return response.json();
}

export const getMyEvents = async (clubLimit?: number) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    const params = new URLSearchParams();
    if (clubLimit) {
        params.set('clubLimit', clubLimit.toString());
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    store.dispatch(setUpdatingUpcomingEvents(true));

    const url = `${process.env.API_URL}/api/events/getMyEvents?${params.toString()}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    store.dispatch(setUpdatingUpcomingEvents(false));

    return response.json();
}

export const getOldClubEvents = async (clubId: string, page: number, limit: number) => {
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', limit.toString());

    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/getOldEvents/${clubId}?${params.toString()}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    return response.json();
}

export const updateClubBasics = async (clubId: string, clubName: string, description: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/clubs/updateClubBasics`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            clubId,
            clubName,
            description
        })
    });
    return response.json();
}

export const removeClub = async (clubId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/clubs/removeClub/${clubId}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });
    return response.json();
}

export const deleteUser = async () => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/user/removeUser`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });
    return response.json();
}

export const checkInvitationUrl = async (link: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/invites/validateInviteLink`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            link
        })
    });
    return response.json();
}

export const removeClubMessage = async (clubId: string, messageId: string) => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/clubs/removeMessage/${messageId}/${clubId}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });
    return response.json();
}

export const addEventComment = async (eventId: string, clubId: string, comment: string) => {

    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/addEventComment`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            eventId,
            clubId,
            comment
        })
    });
    return response.json();
}

export const addInOutComment = async (eventId: string, clubId: string, comment: string) => {

    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/addInOutComment`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            eventId,
            clubId,
            comment
        })
    });
    return response.json();
}


export const sendClubMessage = async (clubId: string, content: string, urgency: "normal" | "important" | "urgent" = "normal") => {
    // Get the current Firebase user's ID token
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    // Get the ID token
    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/clubs/sendMessage`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            clubId,
            content,
            urgency
        })
    });
    return response.json();
}

export const getOldEvent = async (eventId: string, clubId: string) => {
    const auth = await firebaseAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error('No authenticated user found');
    }

    const token = await currentUser.getIdToken();

    const url = `${process.env.API_URL}/api/events/getOldEvent/${clubId}/${eventId}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });

    return response.json();
}




